// Implements: F-IDN-012
/**
 * P2: the two public recovery pages, centred like `/login`. Both sit outside the `401` redirect:
 * the confirmation's `401` means the link is dead, and that is said on its page, not turned into a
 * trip to `/login`. The reset container is keyed by its token, so a second link opened in the same
 * tab starts from a clean form (checklist 14).
 *
 * Once a link ended (saved, or dead), its address is REPLACED by the tokenless `/restablecer`, with
 * the outcome in the history state: the spent token leaves the address bar and the back button.
 * `/restablecer` opened with no outcome to show goes to the request page.
 */

import type { ReactElement } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";

import { ForgotPasswordContainer } from "../../modules/auth/containers/ForgotPasswordContainer";
import { ResetPasswordContainer } from "../../modules/auth/containers/ResetPasswordContainer";
import type { ResetOutcome } from "../../modules/auth/containers/ResetPasswordContainer";
import styles from "./LoginPage.module.css";

export function ForgotPasswordPage(): ReactElement {
  return (
    <main className={styles.page}>
      <ForgotPasswordContainer />
    </main>
  );
}

/** Where a link that ended lands, without its token. */
export const RESET_SETTLED_PATH = "/restablecer";
const RECOVERY_PATH = "/recuperar";

function outcomeOf(state: unknown): ResetOutcome | null {
  if (typeof state !== "object" || state === null) return null;
  const outcome = (state as { outcome?: unknown }).outcome;
  return outcome === "done" || outcome === "gone" ? outcome : null;
}

export function ResetPasswordPage(): ReactElement {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  return (
    <main className={styles.page}>
      <ResetPasswordContainer
        key={token}
        token={token}
        onSettled={(outcome) => {
          void navigate(RESET_SETTLED_PATH, { replace: true, state: { outcome } });
        }}
      />
    </main>
  );
}

export function ResetSettledPage(): ReactElement {
  const outcome = outcomeOf(useLocation().state);
  if (outcome === null) return <Navigate to={RECOVERY_PATH} replace />;
  return (
    <main className={styles.page}>
      <ResetPasswordContainer key={outcome} token="" initialView={outcome} />
    </main>
  );
}
